/**
 * Ações navegáveis do assistente (home + Central de Ajuda).
 * Segurança: só IDs allowlistados / payload validado + rotas internas seguras.
 */

const STATIC_ACTIONS = Object.freeze({
    ver_fila: {
        id: 'ver_fila',
        label: 'Ver redações',
        to: '/documentos?status=pendente',
        variant: 'primary',
    },
    ver_alunos: {
        id: 'ver_alunos',
        label: 'Ver alunos',
        to: '/alunos',
        variant: 'secondary',
    },
    ver_relatorios: {
        id: 'ver_relatorios',
        label: 'Ver relatórios',
        to: '/relatorios',
        variant: 'secondary',
    },
    ver_turmas: {
        id: 'ver_turmas',
        label: 'Ver turmas',
        to: '/turmas',
        variant: 'secondary',
    },
    ver_cursos: {
        id: 'ver_cursos',
        label: 'Ver cursos',
        to: '/cursos',
        variant: 'primary',
    },
    ver_planos: {
        id: 'ver_planos',
        label: 'Ver planos',
        to: '/planos-estudos',
        variant: 'secondary',
    },
    criar_curso: {
        id: 'criar_curso',
        label: 'Criar curso',
        to: '/curso',
        variant: 'secondary',
    },
    abrir_ajuda: {
        id: 'abrir_ajuda',
        label: 'Abrir ajuda',
        to: '/ajuda?tab=doc',
        variant: 'secondary',
    },
    ver_tutoriais: {
        id: 'ver_tutoriais',
        label: 'Ver tutoriais',
        to: '/ajuda?tab=tutoriais',
        variant: 'secondary',
    },
    contato_suporte: {
        id: 'contato_suporte',
        label: 'Falar com suporte',
        to: '/ajuda?tab=contato',
        variant: 'secondary',
    },
});

const ALLOWED_PREFIXES = Object.freeze([
    '/documentos',
    '/alunos',
    '/turmas',
    '/relatorios',
    '/ajuda',
    '/dashboard',
    '/cursos',
    '/curso',
    '/planos-estudos',
    '/plano-estudo',
    '/landing-page',
    '/dashboard-educador-digital',
]);

const YOUTUBE_ID_RE = /^[a-zA-Z0-9_-]{11}$/;

/**
 * @param {unknown} to
 * @returns {boolean}
 */
export function isSafeAssistantPath(to) {
    if (typeof to !== 'string') return false;
    const path = to.trim();
    if (!path.startsWith('/') || path.startsWith('//')) return false;
    if (/^[a-z][a-z0-9+.-]*:/i.test(path)) return false;
    if (/[<>\\"']/.test(path)) return false;
    return ALLOWED_PREFIXES.some(
        (prefix) => path === prefix || path.startsWith(`${prefix}?`) || path.startsWith(`${prefix}/`)
    );
}

/**
 * @param {unknown} raw
 * @param {{ continueTo?: string|null, reviewTo?: string|null }} [opts]
 * @returns {Array<{ id: string, label: string, to: string, variant?: string, youtube_id?: string }>}
 */
export function sanitizeDashboardActions(raw, opts = {}) {
    return sanitizeAssistantActions(raw, opts);
}

/**
 * @param {unknown} raw
 * @param {{ continueTo?: string|null, reviewTo?: string|null }} [opts]
 * @returns {Array<{ id: string, label: string, to: string, variant?: string, youtube_id?: string }>}
 */
export function sanitizeAssistantActions(raw, opts = {}) {
    const list = Array.isArray(raw) ? raw : [];
    const out = [];
    const seen = new Set();

    for (const item of list) {
        if (!item || typeof item !== 'object') continue;
        const id = String(item.id || '').trim().toLowerCase();
        if (!id || seen.has(id)) continue;

        if (id === 'continuar_correcao' || id === 'revisar_agora') {
            const to = String(item.to || opts.continueTo || opts.reviewTo || '').trim();
            if (!isSafeAssistantPath(to)) continue;
            seen.add(id);
            out.push({
                id,
                label: id === 'continuar_correcao' ? 'Continuar correção' : 'Revisar agora',
                to,
                variant: 'primary',
            });
            continue;
        }

        if (id.startsWith('tutorial_')) {
            const to = String(item.to || '').trim();
            if (!isSafeAssistantPath(to) || !to.includes('tab=tutoriais')) continue;
            const youtubeId = String(item.youtube_id || '').trim();
            if (youtubeId && !YOUTUBE_ID_RE.test(youtubeId)) continue;
            seen.add(id);
            out.push({
                id,
                label: String(item.label || 'Assistir tutorial').slice(0, 80),
                to,
                variant: item.variant === 'secondary' ? 'secondary' : 'primary',
                youtube_id: youtubeId || undefined,
            });
            if (out.length >= 4) break;
            continue;
        }

        const catalog = STATIC_ACTIONS[id];
        if (!catalog) continue;
        const to = isSafeAssistantPath(item.to) ? String(item.to).trim() : catalog.to;
        if (!isSafeAssistantPath(to)) continue;

        seen.add(id);
        out.push({
            ...catalog,
            to,
            label: String(item.label || catalog.label).slice(0, 80),
            variant: item.variant === 'primary' || item.variant === 'secondary' ? item.variant : catalog.variant,
        });
        if (out.length >= 4) break;
    }

    return out;
}

/**
 * @param {{
 *   pendingCount?: number,
 *   staleCount?: number,
 *   hasInCorrectionSpotlight?: boolean,
 *   continueTo?: string|null,
 *   reviewTo?: string|null,
 *   weekCorrected?: number,
 * }} snapshot
 */
export function buildWelcomeActions(snapshot = {}) {
    const actions = [];
    const pending = Number(snapshot.pendingCount) || 0;
    const week = Number(snapshot.weekCorrected) || 0;
    const hardest = String(snapshot.hardestCompetenciaNome || '').trim();

    // Nunca duplicar CTAs da home (Revisar agora / Ver redações / Continuar).
    // Preferir valor complementar: tutorial, relatórios, ajuda.
    if (pending > 0) {
        actions.push(STATIC_ACTIONS.abrir_ajuda);
        // tutorial entra via sanitize se o backend/remoto enviar; localmente apontamos tutoriais
        actions.push(STATIC_ACTIONS.ver_tutoriais);
    } else if (hardest || week > 0) {
        actions.push(STATIC_ACTIONS.ver_relatorios, STATIC_ACTIONS.ver_tutoriais);
    } else {
        actions.push(STATIC_ACTIONS.ver_tutoriais, STATIC_ACTIONS.ver_relatorios);
    }

    return sanitizeAssistantActions(actions).slice(0, 2);
}

/**
 * Ações complementares do Painel Educador (não repetem alerta/cards).
 * @param {{ planosPendentes?: number, cursosCount?: number }} [snapshot]
 */
export function buildEducadorWelcomeActions(snapshot = {}) {
    const planos = Number(snapshot.planosPendentes) || 0;
    const cursos = Number(snapshot.cursosCount) || 0;
    const actions = [];

    if (planos > 0) {
        actions.push(STATIC_ACTIONS.ver_planos, STATIC_ACTIONS.abrir_ajuda);
    } else if (cursos === 0) {
        actions.push(STATIC_ACTIONS.criar_curso, STATIC_ACTIONS.ver_tutoriais);
    } else {
        actions.push(STATIC_ACTIONS.ver_cursos, STATIC_ACTIONS.abrir_ajuda);
    }

    return sanitizeAssistantActions(actions).slice(0, 2);
}

/**
 * Thumbnail YouTube oficial (somente IDs já allowlistados no servidor).
 * @param {string} youtubeId
 */
export function youtubeThumbnailUrl(youtubeId) {
    const id = String(youtubeId || '').trim();
    if (!YOUTUBE_ID_RE.test(id)) return null;
    return `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
}

/**
 * Abre tutorial/vídeo em nova guia (YouTube se houver ID válido).
 * @param {{ id?: string, to?: string, youtube_id?: string }|null|undefined} action
 * @returns {boolean} true se a ação foi tratada
 */
export function openAssistantTutorialInNewTab(action) {
    if (!action || typeof action !== 'object') return false;

    const id = String(action.id || '').trim();
    const to = String(action.to || '').trim();
    const youtubeId = String(action.youtube_id || '').trim();

    const isTutorialAction =
        id.startsWith('tutorial_') ||
        (isSafeAssistantPath(to) && to.includes('tab=tutoriais'));

    if (!isTutorialAction) return false;

    if (YOUTUBE_ID_RE.test(youtubeId)) {
        window.open(
            `https://www.youtube.com/watch?v=${encodeURIComponent(youtubeId)}`,
            '_blank',
            'noopener,noreferrer'
        );
        return true;
    }

    if (!isSafeAssistantPath(to)) return false;
    window.open(`${window.location.origin}${to}`, '_blank', 'noopener,noreferrer');
    return true;
}

export { STATIC_ACTIONS as DASHBOARD_ASSISTANT_ACTIONS };
